import { layers } from '@atlaskit/theme';
import Portal from '@atlaskit/portal';
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from '@emotion/styled';
import { calculatePreviewPosition } from 'src/utils/position-helpers';
import { Template } from '../constants/templates';
import { debounce } from 'src/utils/debounce';
import { TemplatePreview } from './TemplatePreview';

const HIDE_TIMEOUT = 250;
const RENDER_DEBOUNCE_TIME = 80;

type ContainerProps = {
  top: number;
  left: number;
  isVisible: boolean;
};

type RenderProps = {
  showPreview: (
    template: Template,
    relativeCoordinates: DOMRect,
    templateIndex: number
  ) => void;
  hidePreview: () => void;
};

const PreviewContainer = styled.div<ContainerProps>`
  top: ${(props) => `${props.top}px`};
  left: ${(props) => `${props.left}px`};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  transition: ${(props) => (props.isVisible ? 'all 100ms linear' : 'none')};
  position: fixed;
`;

interface PopupPreviewProps {
  templates: Template[];
  enabled: boolean;
  children: (props: RenderProps) => ReactNode;
}

export const PopupPreview = ({
  enabled,
  templates,
  children
}: PopupPreviewProps) => {
  const [currentTemplate, setCurrentTemplate] = useState<
    Template | undefined
  >();
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: -1000,
    left: -1000
  });
  const [templateIndex, setTemplateIndex] = useState<number>(-1);

  const previewRef = useRef<HTMLDivElement | null>(null);
  const hideTimeout = useRef<number | null>(null);
  const currentTemplateRef = useRef<Template | undefined>(undefined);

  const debouncedSetTemplateAndIndex = useMemo(
    () =>
      debounce(async (template: Template, templateIndex: number) => {
        setTemplateIndex(templateIndex);
        setCurrentTemplate(template);
        currentTemplateRef.current = template;
      }, RENDER_DEBOUNCE_TIME),
    []
  );

  const resumePreview = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  }, []);

  const showPreview = useCallback(
    async (
      template: Template,
      relativeCoordinates: DOMRect,
      templateIndex: number
    ) => {
      if (!previewRef.current) {
        return;
      }
      if (!enabled) {
        return;
      }

      resumePreview();

      // we use ref, not state (currentTemplate) here, since state should
      // be added as a dependency to useEffect
      // currentTemplate changes quite often, this would lead to
      // change of showPreview handler, and that would result
      // in re-render of component that consumes showPreview via render props
      if (
        currentTemplateRef.current &&
        currentTemplateRef.current.id === template.id
      ) {
        return;
      }

      const position = calculatePreviewPosition(
        previewRef.current.getBoundingClientRect(),
        relativeCoordinates
      );

      setPosition(position);
      await debouncedSetTemplateAndIndex(template, templateIndex);
    },
    [resumePreview, enabled, debouncedSetTemplateAndIndex]
  );

  const hidePreview = useCallback(() => {
    if (hideTimeout.current) {
      return;
    }

    hideTimeout.current = window.setTimeout(() => {
      currentTemplateRef.current = undefined;
      setCurrentTemplate(undefined);
      hideTimeout.current = null;
      setPosition({ top: -1000, left: -1000 });
    }, HIDE_TIMEOUT);
  }, []);

  const renderProps = useMemo(
    () => ({
      showPreview,
      hidePreview
    }),
    [showPreview, hidePreview]
  );

  useEffect(() => {
    window.addEventListener('resize', hidePreview);
    return () => window.removeEventListener('resize', hidePreview);
  }, [hidePreview]);

  return (
    <Fragment>
      <Portal zIndex={layers.tooltip()}>
        <PreviewContainer
          ref={previewRef}
          onMouseEnter={resumePreview}
          onMouseLeave={hidePreview}
          top={position.top}
          left={position.left}
          isVisible={!!currentTemplate && enabled}
        >
          <TemplatePreview
            templates={templates}
            currentTemplateIndex={templateIndex}
          />
        </PreviewContainer>
      </Portal>
      {children(renderProps)}
    </Fragment>
  );
};
